import { Box, InputBase, IconButton } from "@mui/material";
import { useTheme } from "@mui/material";
import { tokens } from "../../../../../theme";
import Shbutton from "../../../../theme/buttons/Shbutton";
import SaveIcon from '@mui/icons-material/Save';
import Text from "../../../../theme/text/Text";
import Dropdown from "../../../../theme/dropout/DropOut";
import { useEffect, useState } from "react";
import ButtonGroup from "../../../../theme/buttons/ButtonGroup";
import { useEditShipperMutation, useGetExternalObjectsQuery, useGetConfigInfoQuery, useSyncSenderCodesMutation } from "../../../../../newapi/shipper/shipperSlice";
import ShValidatedInput from "../../../../theme/inputs/ShValidatedInput";
import * as yup from "yup";
import { yupRequired } from "../../../../../utils/validation";
import ShTextSkeleton from "../../../../theme/skeleton/ShTextSkeleton";
import DeleteIcon from '@mui/icons-material/Delete';

import { useTranslation } from "react-i18next";
import { showToastMessage, showToastMessageRequestError, showToastMessageUpdateRequest } from "../../../../../utils/toasts";
import { useSelector } from "react-redux";
import { selectUserPermission } from "../../../../../newapi/user/authentication/authSlice";
import { UserPrivileges } from '../../../../../utils/permissions';
import { shipperConfigurations } from "../../../../../data/shipperConfigurations";
import EMGeneralExplainerBox from "../../../ModalsPopover/explainers/EMGeneralExplainerBox";
import { InputDropdown } from "../../../../theme/dropdowns/InputDropdown";
import cx from 'classnames';
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import UniversalConnectionTester from "../../../ModalsProgression/MpAddShipper/components/UniversalConnectionTester";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useGetRelationsSelectQuery } from "../../../../../newapi/global/relation/relationSlice";
import SpDesktopFooter from "../../../../theme/sidepanel2/SidepanelFooter";
import RelationAuthFields, { SHIPPER_AUTH_FIELDS } from "../components/RelationAuthFields";

const GridActions = (props) => {
    const theme = useTheme();
    const { t } = useTranslation();

    return (
        <ButtonGroup version={2}>
            {props.hasModifyPermission && (
            <Shbutton
                isLoading={props.isLoading}
                className={"group-btn"}
                onClick={props.action}
                color={theme.palette.mode === "dark" ? "blue" : "primary"}
                variant="contained"
                endIcon={<SaveIcon />}
            >
                {t('save')}
            </Shbutton>
            )}
        </ButtonGroup>
    );
};

const AuthorisationPanel = (props) => {
    const  { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const hasModifyPermission = useSelector((state) => 
        selectUserPermission(state, [UserPrivileges.SHIPPER_MANAGE, UserPrivileges.SETTINGS_ALL])
    );

    const [shipperInfo, setShipperInfo] = useState();
    const [formData, setFormData] = useState({});
    const [config, setConfig] = useState({});
    const [open, setOpen] = useState(false);
    const [connectionTestResult, setConnectionTestResult] = useState(null);
    const [relationKeys, setRelationKeys] = useState([]);


    // Only fetch brands if type is sendcloud
    const isSendcloud = props.data?.type === 'sendcloud';
    const { data: externalObjects } = useGetExternalObjectsQuery(
        isSendcloud ? { id: props.data?.id, type: 'brands' } : skipToken
    );

    // Only fetch config info if type is zineps (for the default sender address dropdown)
    const isZineps = props.data?.type === 'zineps';
    const { data: zinepsConfigInfo } = useGetConfigInfoQuery(isZineps ? props.data?.id : skipToken);

    // Only fetch config info if type is vinologix (for the default sender address dropdown)
    const isVinologix = props.data?.type === 'vinologix';
    const { data: vinologixConfigInfo } = useGetConfigInfoQuery(isVinologix ? props.data?.id : skipToken);

    // Parcel stores its sender code list on the shipper configuration; a sender code prints the relation address as sender
    const isParcel = props.data?.type === 'parcel';
    const [syncSenderCodes, { isLoading: syncingSenderCodes }] = useSyncSenderCodesMutation();

    const { data: relationOptions, isLoading: relationsLoading} = useGetRelationsSelectQuery();
  
    useEffect(() => {
        if(props.data){
            setShipperInfo(props.data);
            const shipperType = props.data.type;
            const config = shipperConfigurations[shipperType] || { fields: [], saveFormat: () => ({}) };
            setConfig(config);
            setFormData(props.data.configuration ?? {});

            // Always reset relationKeys - either from data or empty array
            if (props.data.additionalConfiguration?.relationKeys) {
                const keys = props.data.additionalConfiguration.relationKeys;

                // Sort relation keys by name from relationOptions
                if (relationOptions && keys.length > 0) {
                    const sortedKeys = [...keys].sort((a, b) => {
                        const relationA = relationOptions.find(r => r.id === a.id);
                        const relationB = relationOptions.find(r => r.id === b.id);

                        const nameA = relationA?.name?.toLowerCase() || '';
                        const nameB = relationB?.name?.toLowerCase() || '';

                        return nameA.localeCompare(nameB);
                    });
                    setRelationKeys(sortedKeys);
                } else {
                    setRelationKeys(keys);
                }
            } else {
                setRelationKeys([]);
            }
        }
    },[props.data, relationOptions]);

    useEffect(() => {
       // console.log("config", JSON.stringify(config));
    },[config]);

    useEffect(() => {
        // Only add default relation key for shipper types that have auth fields configured
        const supportsRelationKeys = SHIPPER_AUTH_FIELDS[props.data?.type];

        if (supportsRelationKeys && (relationKeys.length === 0 || !relationKeys.some(x => x.id === 0))) {
            addRelationKey();
        }
    }, [relationKeys, props.data?.type])

    const addRelationKey = () => {
        const newKey = { id: 0 };

        // Initialize fields based on shipper type using SHIPPER_AUTH_FIELDS
        const authFields = SHIPPER_AUTH_FIELDS[props.data?.type];
        if (authFields) {
            authFields.forEach(field => {
                newKey[field.name] = "";
            });
        }

        setRelationKeys(prev => [...prev, newKey]);
    }

    const handleBoolChange = (name, value) => { 
        if(value === "true"){
            setFormData(prevData => ({ ...prevData, [name]: true }));
        }else{
            setFormData(prevData => ({ ...prevData, [name]: false }));
        }
    };

    const handleInputChange = (name, value) => {
        setFormData(prevData => ({ ...prevData, [name]: value }));
    }


    const handleDropdownChange = (baseList, name, value) => {
        setFormData(prevData => ({ ...prevData, [name]: baseList[value].value}));
    };
    
    const handleMultiDropdownChange = (baseList, name, values) => {
        setFormData(prevData => {
            const updatedArray = values.map(index => baseList[index].value);
            return { ...prevData, [name]: updatedArray };
        });
    };

    const handleRelationKeyChange = (field, index, value) => {
        if (field === "id" && relationKeys.some(x => x.id === value)) {
            showToastMessage("error", t('relation_has_a_key_already'));
            return;
        }

        setRelationKeys(prev => prev.map((item, i) =>
            i === index ? { ...item, [field]: value } : item
        ));
    }

    const handleDeleteRelationKey = (index) => {
        setRelationKeys(prev => prev.filter((x, i) => i !== index));
    }

    const handleConnectionTestResult = (result) => {
        setConnectionTestResult(result);
    };

    const handleSyncSenderCodes = async () => {
        if (syncingSenderCodes) return;
        try {
            const result = await syncSenderCodes(props.data.id).unwrap();
            const senderCodes = result.senderCodes ?? [];
            setFormData(prevData => ({ ...prevData, senderCodes: senderCodes }));
            showToastMessage("success", t("sender_codes_fetched", { count: senderCodes.length }));
        } catch (err) {
            showToastMessageRequestError(t("fetch_sender_codes_failed"), t, err);
        }
    };

    const selectedFieldIndex = (field) => {
        if(Array.isArray(formData[field.name])){
            const selectedIndexes = formData[field.name].map(item => field.options.findIndex(option => option.value === item));
            return selectedIndexes;
        } else {
            return [];
        }
    }

    
    const [fieldErrors, setFieldErrors] = useState({});


    const [EditShipper,{isLoading}] = useEditShipperMutation();
    const handleSaveClick = async () => {
        try {
            if (isLoading) return;

            const updateObj = {
                id: shipperInfo.id,
                configuration: formData,
            }

            if (relationKeys.length > 0) {
                const authFields = SHIPPER_AUTH_FIELDS[props.data?.type];

                let filteredKeys;
                if (authFields) {
                    // Filter keys where all required fields have values; optional fields may stay empty
                    filteredKeys = relationKeys.filter(x => {
                        if (x.id === 0) return false;
                        return authFields.every(field => field.optional || x[field.name]?.length > 0);
                    });
                } else {
                    filteredKeys = [];
                }

                if (filteredKeys.length > 0) {
                    updateObj.additionalConfiguration = {relationKeys: filteredKeys};
                }
            }

            const data = await EditShipper(updateObj).unwrap()
            showToastMessageUpdateRequest(t, data);
        } catch (err) {
            if (err.name === 'ValidationError') {
                // Create an errors object where keys are field names and values are error messages
                const errors = err.inner.reduce((acc, currentError) => ({
                    ...acc,
                    [currentError.path]: currentError.message
                }), {});
            
                // Set the validation errors in the state
                setFieldErrors(errors);
            } else {
                // Handle other errors, such as network errors
                showToastMessageRequestError(t("update_failed"), t, err);
            }
        }       
    };

    useEffect(() => {
        props.replaceView({
            ...props.config,
            mobileActions:
                <GridActions
                    isLoading={isLoading}
                    id={props.data?.id}
                    action={handleSaveClick}
                    hasModifyPermission={hasModifyPermission}
                />,
        });

        return () => {
            // console.log("unmounting warehouse");
        }
    },[isLoading,props.data?.id,handleSaveClick,hasModifyPermission]);
    
    return (
       <Box height={1} display={"flex"} flexDirection={"column"} paddingX={3}
            sx={{
                "@media screen and (min-width: 48.1em)"  :{
                    marginBottom:11
                }
            }}
        >
            <Dropdown title={t("basic")} open>
            {config?.fields?.map(field => {
                switch (field.type) {
                    case 'text':
                        return (
                            <Box key={field.name} sx={{
                                display:"flex",
                                flexDirection:"column",
                                gap:1,
                                paddingTop:3,
                            }}>
                                <Text>{t(field.label)}{field.tooltip && <EMGeneralExplainerBox title={t(field.label)} text={t(field.tooltip)}/>}</Text>
                                <ShValidatedInput
                                    name={field.name}
                                    label={t(field.label)}
                                    value={formData[field.name] ?? ''}
                                    onChange={handleInputChange}
                                    //error={props.errors.name}
                                />
                                {/* <InputBase
                                    value={formData[field.name] || ''}
                                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                                /> */}
                            </Box>
                        );
                     case 'password':
                                        return (
                                            <Box key={field.name} sx={{
                                                display:"flex",
                                                flexDirection:"column",
                                                gap:1,
                                                paddingTop:3,
                                            }}>
                                                <Text>{t(field.label)}{field.tooltip && <EMGeneralExplainerBox title={t(field.label)} text={t(field.tooltip)}/>}</Text>
                                                <ShValidatedInput
                                                    name={field.name}
                                                    label={t(field.label)}
                                                    value={formData[field.name] || ''}
                                                    onChange={handleInputChange}
                                                    type={"password"}
                                                    //error={props.errors.name}
                                                />
                                                {/* <InputBase
                                                    value={formData[field.name] || ''}
                                                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                                                /> */}
                                            </Box>
                                        );
                    case 'boolean':
                        return (
                            <Box 
                        display={"flex"} 
                        flexDirection={"column"}
                        sx={{
                            gap:1,
                            paddingTop:3,
                            maxWidth: 110,
                            "@media screen and (max-width:47.9375em)" : {
                                width:1,
                                flexDirection:"row",
                                alignItems:"center",
                                justifyContent:"space-between",
                            }
                        }}
                        >
                        <Text>
                        {t(field.label)}
                        {field.tooltip && <EMGeneralExplainerBox title={t(field.label)} text={t(field.tooltip)}/>}
                        </Text>
                        
                        <ToggleButtonGroup
                            color={formData[field.name] ? "green" : "red"}
                            value={formData[field.name] ? "true" : "false"}
                            exclusive
                            onChange={(e) => handleBoolChange(field.name, e.target.value)}
                            aria-label="Active"

                                className={cx(`${formData[field.name]}`, {
                                    'active': formData[field.name]
                                })}
                                sx={{
                                    background: colors.red[100],
                                    padding:0.5,
                                    borderRadius:5,
                                    transition:"250ms",
                                    "&.active" : {
                                        background:colors.green[100],
                                        transition:"250ms",
                                    },
                                }}
                            >
                                <ToggleButton 
                                    sx={{
                                        padding:0,
                                        paddingX:2,
                                        border:0,
                                        lineHeight:"32px",
                                        textTransform:"unset",
                                        borderRadius:"32px !important",
                                        color:colors.green[400],
                                        transition:"250ms",
                                        "&.Mui-selected,&.Mui-selected:hover" : {
                                            background:colors.red[400],
                                            color:`${theme.palette.mode === "dark" ? "rgba(0,0,0,.75)" : "#fff"}`
                                        }
                                    }}
                                    value="false">
                                    {t('no')}
                                </ToggleButton>
                                <ToggleButton 
                                    sx={{
                                        padding:0,
                                        paddingX:2,
                                        border:0,
                                        lineHeight:"32px",
                                        textTransform:"unset",
                                        borderRadius:"32px !important",
                                        color:colors.red[400],
                                        transition:"250ms",
                                        "&.Mui-selected,&.Mui-selected:hover" : {
                                            background:colors.green[400],
                                            color:`${theme.palette.mode === "dark" ? "rgba(0,0,0,.75)" : "#fff"}`
                                        }
                                    }}
                                    value="true"
                                >
                                    {t('yes')}
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                            );
                            case 'dropdown':
                                return (
                                    <Box key={field.name} sx={{
                                        display:"flex",
                                        flexDirection:"column",
                                        gap:1,
                                        paddingTop:3,
                                    }}>
                                        <Text>{t(field.label)}{field.tooltip && <EMGeneralExplainerBox title={t(field.label)} text={t(field.tooltip)}/>}</Text>
                                        
                                        <InputDropdown
                                            key={formData[field.name]}
                                            name={field.name}
                                            noSelection={t('choose_option')}
                                            useTranslation={field.translate}
                                            displayName="label"
                                            options={field.options ?? ""}
                                            selected={field.options.findIndex(option => option.value === formData[field.name])}
                                            onChange={(selected) => handleDropdownChange(field.options,field.name, selected)}
                                            closeOnSelection
                                        />
                                    </Box>
                                );
                            case 'multidropdown':
                                return (
                                    <Box key={field.name} sx={{
                                        display:"flex",
                                        flexDirection:"column",
                                        gap:1,
                                        paddingTop:3,
                                    }}>
                                        <Text>{t(field.label)}{field.tooltip && <EMGeneralExplainerBox title={t(field.label)} text={t(field.tooltip)}/>}</Text>
                                        <InputDropdown
                                            key={formData[field.name]}
                                            name={field.name}
                                            multiple={true}
                                            noSelection={t('choose_option')}
                                            displayName="label"
                                            options={field.options ?? []}
                                            selected={selectedFieldIndex(field)}
                                            onChange={(selected) => handleMultiDropdownChange(field.options,field.name, selected)}
                                            closeOnSelection={false}
                                        />
                                    </Box>
                                );
                        default:
                            return null;
                    }
                })}

                {/* Sendcloud brand id */}
                {isSendcloud && externalObjects && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, paddingTop: 3 }}>
                        <Text>{t("brand_id")}</Text>
                        <InputDropdown
                            key={formData.brandId}
                            name="brandId"
                            noSelection={t('choose_option')}
                            displayName="name" // or "label" depending on externalObjects structure
                            options={externalObjects}
                            selected={externalObjects.findIndex(option => option.id === formData.brandId)}
                            onChange={selected => handleInputChange("brandId", externalObjects[selected].id)}
                            closeOnSelection
                        />
                    </Box>
                )}

                {/* Zineps default sender address */}
                {isZineps && Array.isArray(zinepsConfigInfo?.configInfo?.senders) && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, paddingTop: 3 }}>
                        <Text>{t("sender_address")}</Text>
                        <InputDropdown
                            key={formData.senderId}
                            name="senderId"
                            noSelection={t('choose_option')}
                            displayName="key"
                            options={zinepsConfigInfo.configInfo.senders}
                            selected={zinepsConfigInfo.configInfo.senders.findIndex(option => option.value === formData.senderId)}
                            onChange={selected => handleInputChange("senderId", zinepsConfigInfo.configInfo.senders[selected].value)}
                            closeOnSelection
                        />
                    </Box>
                )}

                {/* Vinologix default sender address */}
                {isVinologix && Array.isArray(vinologixConfigInfo?.configInfo?.senderAddresses) && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, paddingTop: 3 }}>
                        <Text>{t("vinologix_sender_address")}</Text>
                        <InputDropdown
                            key={formData.senderAddress}
                            name="senderAddress"
                            noSelection={t('choose_option')}
                            displayName="key"
                            options={vinologixConfigInfo.configInfo.senderAddresses}
                            selected={vinologixConfigInfo.configInfo.senderAddresses.findIndex(option => option.value === formData.senderAddress)}
                            onChange={selected => handleInputChange("senderAddress", vinologixConfigInfo.configInfo.senderAddresses[selected].value)}
                            closeOnSelection
                        />
                    </Box>
                )}

                {/* Parcel sender codes */}
                {isParcel && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, paddingTop: 3 }}>
                        <Text>{t("sender_codes")}<EMGeneralExplainerBox title={t("sender_codes")} text={t("sender_codes_help")}/></Text>
                        {Array.isArray(formData.senderCodes) && formData.senderCodes.length > 0 ? (
                            formData.senderCodes.map(code => (
                                <Text key={code.senderCode}>{code.senderCode} - {code.senderName}</Text>
                            ))
                        ) : (
                            <Text>{t("no_sender_codes")}</Text>
                        )}
                        {hasModifyPermission && (
                            <Box>
                                <Shbutton
                                    isLoading={syncingSenderCodes}
                                    onClick={handleSyncSenderCodes}
                                    color={theme.palette.mode === "dark" ? "blue" : "primary"}
                                    variant="contained"
                                >
                                    {t("fetch_sender_codes")}
                                </Shbutton>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Universal Connection Tester */}
                {config.testConnection && (
                    <UniversalConnectionTester
                        shipperType={shipperInfo?.type}
                        formData={formData}
                        onTestResult={handleConnectionTestResult}
                    />
                )}
            </Dropdown>
            {SHIPPER_AUTH_FIELDS[props.data?.type] && (
                <Dropdown title={t("relations")} open>
                    <Box sx={{
                    display:"flex",
                    justifyContent:"space-between",
                    "@media screen and (max-width: 47.9375em)" : {
                        flexDirection:"column"
                    }
                }}>
                    
                    {/* Two columns */}
                    
                    <Box flex={1}
                        sx={{
                            display:"flex",
                            gap: 1.5,
                            justifyContent:"flex-start",
                            flexDirection:"column"
                        }}
                    >
                        {isLoading ?

                            <Box
                                sx={{
                                    display:"flex",
                                    gap:2,
                                    alignItems:"center",
                                }}
                            >
                                <ShTextSkeleton/>
                                <ShTextSkeleton/>
                                <ShTextSkeleton/>
                            </Box>

                        :
                        <RelationAuthFields
                            shipperId={props.data?.id}
                            shipperType={props.data?.type}
                            relationKeys={relationKeys}
                            relationOptions={relationOptions}
                            onRelationKeyChange={handleRelationKeyChange}
                            onDeleteRelationKey={handleDeleteRelationKey}
                        />
                        }
                        </Box>
                    </Box>
                </Dropdown>
            )}
            <SpDesktopFooter>
                <GridActions
                    isLoading={isLoading}
                    id={props.data?.id}
                    action={handleSaveClick}
                    hasModifyPermission={hasModifyPermission}
                />
            </SpDesktopFooter>
            {/* <Box
                sx={{
                    position:"absolute",
                    bottom:0,
                    right:0,
                    padding:3,
                    width:1,
                    background:theme.palette.mode === "dark" ? colors.bg["tertiary"] : colors.grey[0],
                    "@media screen and (min-width: 47.9376em) and (max-width: 63.9375em)" : {
                        padding:2,
                        paddingY:2.5,
                    }
                }}
            >
                <GridActions
                    isLoading={isLoading}
                    id={props.data?.id}
                    action={handleSaveClick}
                    hasModifyPermission={hasModifyPermission}
                />
            </Box> */}
        </Box>
    );
}

export default AuthorisationPanel;